import React, { useContext, useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { TOUR_PENDING_KEY, resumeQrStage, resumeTimezoneStage, resumePriceTierStage } from '../Shared/WhatsNew/tours';
import { resumeOnboardingSettings } from '../Shared/WhatsNew/onboardingTour';

// Dependencies
import { CompanyLogoContext } from '../../Context/CompanyLogoContextProvider';
import {
  uploadLogo as uploadLogoAction,
  setLogoLink,
  // All 15 config toggles below are now loaded in one batched call
  // (getSettingsPageConfig); only their setters remain imported here.
  setManufacturerSwitchPosition,
  setDistributorSwitchPosition,
  setRoomCalcSwitchPosition,
  setFreightSwitchPosition,
  setLocationSwitchPosition,
  setCustomerCreateAccountsSwitchPos,
  setProdCheckoutSwitchPosition,
  setProdFavSwitchPosition,
  getMaterialClassCodes,
  getCodeTranslations,
  getUom,
  getQrPrintConfig,
  setNotificationsSwitchPos,
  getNotificationsSwitchPos,
  setRoomvoToggle,
  updateLocation,
  getSampleReminderConfig,
  setSampleReminderConfig,
  getCatalogCsv,
  setQconnectConfig,
  getQconnectConfig,
  setShowCollectionToCustomer,
  setShouldRequireOptionSelection,
  setCombineStyleSizesSelection,
  setShowSpecsToCustomer,
  setShouldRequireLoginSelection,
  setPriceTierMode,
  getIntegrationConfig,
  getBarcodeConfig,
  setShowProductDiscoverySelection,
  setShowRelatedProductsSelection,
  getSettingsPageConfig,
} from '../../Actions';
import { AlertContext } from '../../Context/AlertContextProvider';
import MaterialClassSettings from './MaterialClassSetting';
import UomSettings from './UomSettings';
import QrPrintSetting from './QrPrintSettings';
import QrStylePresets from './QrStylePresets';
import PriceTierBands from './PriceTierBands';
import IntegrationSettings from './IntegrationSettings';
import SettingsNav from './SettingsNav';
import IntegrationsPanel from './IntegrationsPanel';
import SectionHeading from './SectionHeading';
import SettingRow from './SettingRow';
import SaveButton from './SaveButton';
import { GROUP_HEADING } from './typography';
import BarcodeSettings from './BarcodeSettings';

// MUI
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import ToolTip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Switch from '@material-ui/core/Switch';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
  Button,
  Divider,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  CircularProgress,
  Backdrop,
} from '@material-ui/core';

// Styles
import '../styles/Admin.css';
import { AdminContext } from '../../Context/AdminContextProvider';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { AppContext } from '../../Context/AppContextProvide';
import { useTimezone } from '../../Context/TimezoneContextProvider';
import TimezonePicker from '../Shared/TimezonePicker';
import { zoneLabel } from '../../Util/timezone';


// Settings sections, in left-nav order. Grouped by what an admin is trying to do
// rather than by which table a setting happens to live in.
const SECTIONS = [
  { id: 'general', label: 'General', hint: 'Logo, registration, timezone' },
  { id: 'productPage', label: 'Product Page', hint: 'What customers see' },
  { id: 'qrPrint', label: 'QR Printing', hint: 'Labels and margins', tourId: 'tour-qr-settings-nav' },
  { id: 'integrations', label: 'Integrations', hint: 'Connected systems', tourId: 'tour-integrations' },
  { id: 'notifications', label: 'Notifications', hint: 'Customer alerts' },
  { id: 'imports', label: 'Imports', hint: 'Price file handling' },
];

const INTEGRATION_TITLES = {
  albi: 'Albi',
  qconnect: 'QConnect',
  roomvo: 'Roomvo',
  barcode: 'RFMS Product Barcode',
  notifications: 'Sample Checkout Reminders',
};

const LogoLinkUpdate = () => {
  const history = useHistory();
  const { handleAlertOpen, setMessageType, setMessage } =
    useContext(AlertContext);
  const { locations, setUpdate: updateAdminContext } = useContext(AdminContext);
  const { user } = useContext(AppContext);

  const { logoLink, setUpdate: setLogoUpdate, companyLogo } = useContext(CompanyLogoContext);
  const { defaultZone, saveDefaultTimezone } = useTimezone();
  const [instanceTimezone, setInstanceTimezone] = useState('');
  const [savingInstanceTz, setSavingInstanceTz] = useState(false);
  const [companyLink, setCompanyLink] = useState('www.yoursite.com');
  const [file, setFile] = useState();
  const hiddenFileInput = useRef(null);
  const [switchState, setSwitchState] = useState(false);
  const [distributorSwitchState, setDistributorSwitchState] = useState(false);
  const [roomvoSwitchState, setRoomvoSwitchState] = useState(false);
  const [roomCalcSwitchState, setRoomCalcSwitchState] = useState(false);
  const [freightSwitchState, setFreightSwitchState] = useState(false);
  const [locationSwitchState, setLocationSwitchState] = useState(false);
  const [userRegSwitchState, setUserRegSwitchState] = useState(false);
  const [notificationsSwitchState, setNotificationsSwitchState] =
    useState(false);
  const [prodCheckoutSwitchState, setProdCheckoutSwitchState] = useState(false);
  const [prodFavSwitchState, setProdFavSwitchState] = useState(false);
  const [prodCollectionSwitchState, setProdCollectionSwitchState] = useState(false);
  const [prodDiscoverySwitchState, setProdDiscoverySwitchState] = useState(false);
  const [prodRelatedSwitchState, setProdRelatedSwitchState] = useState(false);
  const [prodSpecsSwitchState, setProdSpecsSwitchState] = useState(false);
  const [prodReqSelSwitchState, setProdReqSelSwitchState] = useState(false);
  const [prodReqLoginSwitchState, setProdReqLoginSwitchState] = useState(false);
  const [priceTierSwitchState, setPriceTierSwitchState] = useState(false);
  const [prodCombineStyleSizesSwitchState, setProdCombineStyleSizesSwitchState] = useState(false);
  const [open, setOpen] = useState(false);
  const [matClassCdArray, setMatClassCdArray] = useState([]);
  const [codeTranslationArray, setCodeTranslationArray] = useState([]);
  const [uomArray, setUomArray] = useState([]);
  const [dialogSelect, setDialogSelect] = useState('');
  const [margins, setMargins] = useState([]);
  const [isSampleReminderDialogOpen, setIsSampleReminderDialogOpen] =
    useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [tempLocationName, setTempLocationName] = useState('');
  const [tempLocationEmail, setTempLocationEmail] = useState('');
  const [tempLocationPhone, setTempLocationPhone] = useState('');
  const [checkoutPeriod, setCheckoutPeriod] = useState('');
  const [pastDueLimit, setPastDueLimit] = useState('');
  const [pastDueFrequency, setPastDueFrequency] = useState('');
  const [dailyStart, setDailyStart] = useState('');
  const [dailyEnd, setDailyEnd] = useState('');
  const [refreshData, setRefreshData] = useState(true);
  const [qconnectToggle, setQconnectToggle] = useState(false);
  const [qconnectUser, setQconnectUser] = useState("");
  const [qconnectPass, setQconnectPass] = useState("");
  // Snapshots of what is actually stored, so each Save knows whether there is
  // anything to save. Without these the buttons were always enabled and always
  // green, which told you nothing.
  const [savedQconnect, setSavedQconnect] = useState({ username: "", password: "" });
  const [savedNotifConfig, setSavedNotifConfig] = useState(null);
  const [isCatalogProcessing, setIsCatalogProcessing] = useState(false);
  const [isSettingsDataLoading, setIsSettingsDataLoading] = useState(true);
  const [section, setSection] = useState('general');
  // Summary only — never the key itself; the server returns a masked hint.
  const [albiStatus, setAlbiStatus] = useState({ hasKey: false, enabled: false, mapped: 0 });
  // The barcode row's dot was hardcoded off. Its state lives behind its own
  // endpoint, so it is read here alongside the others.
  const [barcodeEnabled, setBarcodeEnabled] = useState(false);
  const [configuring, setConfiguring] = useState(null);

  // Sections mean a tour's target element is not in the DOM unless its section
  // is showing, so each pending tour selects its section before the driver runs.
  // The existing delay covers the re-render.
  useEffect(() => {
    const pending = sessionStorage.getItem(TOUR_PENDING_KEY);
    if (!pending) return;

    const TOUR_SECTIONS = {
      qr: 'qrPrint',
      timezone: 'general',
      priceTiers: 'productPage',
      'onboarding-settings': 'qrPrint',
    };
    const RESUME = {
      qr: () => resumeQrStage(history),
      timezone: () => resumeTimezoneStage(history),
      priceTiers: () => resumePriceTierStage(),
      'onboarding-settings': () => resumeOnboardingSettings(history),
    };

    const resume = RESUME[pending];
    if (!resume) return;

    sessionStorage.removeItem(TOUR_PENDING_KEY);
    if (TOUR_SECTIONS[pending]) setSection(TOUR_SECTIONS[pending]);
    setTimeout(resume, 500);
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      const location = locations.find(l => l.location_id === selectedLocation);
      setTempLocationEmail(location?.location_email ?? '');
      setTempLocationPhone(location?.location_phone ?? '');
      setTempLocationName(location?.customer_facing_name ?? '');
    }
  }, [selectedLocation])

  useEffect(() => {
    async function getPageData() {
      const isOn = (v) => Number(v) === 1;

      // All 15 config toggles in one batched call instead of 15 sequential GETs.
      // Returns { configs: { KEY: { app_value, app_value_text } } }; some toggles
      // live in app_value, the "active"-style ones in app_value_text.
      const configPromise = getSettingsPageConfig()
        .then((result) => {
          const configs = result.data.configs ?? {};
          const val = (key) => configs[key]?.app_value;
          const text = (key) => configs[key]?.app_value_text;

          setProdCheckoutSwitchState(isOn(val('PRODUCT_CHECKOUT')));
          setProdFavSwitchState(isOn(val('PRODUCT_FAVORITE')));
          setUserRegSwitchState(isOn(val('CUSTOMER_CREATE_ACCOUNT')));
          setSwitchState(isOn(text('SHOW_MANUFACTURER_TO_CUSTOMER')));
          setDistributorSwitchState(isOn(text('SHOW_DISTRIBUTOR_TO_CUSTOMER')));
          setRoomCalcSwitchState(isOn(text('SHOW_ROOM_CALCULATOR')));
          setFreightSwitchState(isOn(text('SHOW_FREIGHT_TO_CUSTOMER')));
          setLocationSwitchState(isOn(text('SHOW_LOCATION_TO_CUSTOMER')));
          setRoomvoSwitchState(isOn(val('ROOMVO_TOGGLE')));
          setProdReqLoginSwitchState(isOn(val('REQUIRE_LOGIN_FOR_PRICING')));
          setPriceTierSwitchState(isOn(val('PRICE_TIER_MODE')));
          setProdSpecsSwitchState(isOn(val('SHOW_SPECS_TO_CUSTOMER')));
          setProdCollectionSwitchState(isOn(val('SHOW_COLLECTION_TO_CUSTOMER')));
          setProdReqSelSwitchState(isOn(val('REQUIRE_OPTION_SELECTION')));
          setProdCombineStyleSizesSwitchState(isOn(val('COMBINE_STYLE_SIZES')));
          setProdDiscoverySwitchState(isOn(val('SHOW_PRODUCT_DISCOVERY')));
          setProdRelatedSwitchState(isOn(val('SHOW_RELATED_PRODUCTS')));

          const qconnectOn = isOn(val('QCONNECT_TOGGLE'));
          setQconnectToggle(qconnectOn);

          // Credentials are a separate (sensitive) call — only fetch when enabled.
          if (qconnectOn) {
            return getQconnectConfig()
              .then((qResult) => {
                const { username, password } = qResult.data;
                setQconnectUser(username ?? "");
                setQconnectPass(password ?? "");
                setSavedQconnect({ username: username ?? "", password: password ?? "" });
              })
              .catch(() => {});
          }
        })
        .catch(() => {
          setProdCheckoutSwitchState(false);
          setProdFavSwitchState(false);
          setUserRegSwitchState(false);
          setSwitchState(false);
          setDistributorSwitchState(false);
          setRoomCalcSwitchState(false);
          setFreightSwitchState(false);
          setLocationSwitchState(false);
          setRoomvoSwitchState(false);
          setProdReqLoginSwitchState(false);
          setPriceTierSwitchState(false);
          setProdSpecsSwitchState(false);
          setProdCollectionSwitchState(false);
          setProdReqSelSwitchState(false);
          setProdCombineStyleSizesSwitchState(false);
          setProdDiscoverySwitchState(false);
          setProdRelatedSwitchState(false);
          setQconnectToggle(false);
        });

      // These hit different tables/shapes so they stay separate — but run the whole
      // set concurrently instead of awaiting one at a time (was ~22 serial trips).
      await Promise.all([
        configPromise,
        getQrPrintConfig()
          .then((result) => setMargins(result.data.data[0].config ?? []))
          .catch(() => setMargins([])),
        getUom()
          .then((result) => setUomArray(result.data.data ?? []))
          .catch(() => setUomArray([])),
        getCodeTranslations()
          .then((result) => setCodeTranslationArray(result.data.data ?? []))
          .catch(() => setCodeTranslationArray([])),
        getMaterialClassCodes()
          .then((result) => setMatClassCdArray(result.data.data ?? []))
          .catch(() => setMatClassCdArray([])),
        getNotificationsSwitchPos()
          .then((result) => setNotificationsSwitchState(Number(result.data.sample_reminder) === 1))
          .catch(() => setNotificationsSwitchState(false)),
        getSampleReminderConfig()
          .then((result) => {
            setCheckoutPeriod(Math.floor(result.data.checkoutPeriod) ?? '');
            setPastDueLimit(Math.floor(result.data.pastDueLimit) ?? '');
            setPastDueFrequency(Math.floor(result.data.pastDueFrequency) ?? '');
            setDailyStart(Math.floor(result.data.dailyStart) ?? '');
            setDailyEnd(Math.floor(result.data.dailyEnd) ?? '');
            setSavedNotifConfig({
              checkoutPeriod: String(Math.floor(result.data.checkoutPeriod) ?? ''),
              pastDueLimit: String(Math.floor(result.data.pastDueLimit) ?? ''),
              pastDueFrequency: String(Math.floor(result.data.pastDueFrequency) ?? ''),
              dailyStart: String(Math.floor(result.data.dailyStart) ?? ''),
              dailyEnd: String(Math.floor(result.data.dailyEnd) ?? ''),
            });
          })
          .catch(() => {
            setCheckoutPeriod('');
            setPastDueLimit('');
            setPastDueFrequency('');
            setDailyStart('');
            setDailyEnd('');
          }),
      ]);

  if (isSettingsDataLoading) {
        setIsSettingsDataLoading(false);
      }
    }

    if (logoLink.length) {
      setCompanyLink(logoLink);
    }

    if (refreshData) {
      getPageData();
      setRefreshData(false);
    }
  }, [refreshData]);

  useEffect(() => {
    if (logoLink.length) {
        setCompanyLink(logoLink);
    }
  }, [logoLink]);

  useEffect(() => {
    if (defaultZone) setInstanceTimezone(defaultZone);
  }, [defaultZone]);

  const handleInstanceTimezoneSave = async () => {
    if (!instanceTimezone) return;
    setSavingInstanceTz(true);
    try {
      await saveDefaultTimezone(instanceTimezone);
      setMessage('Default timezone updated');
      setMessageType('success');
      handleAlertOpen();
    } catch (err) {
      setMessage('Error updating default timezone');
      setMessageType('error');
      handleAlertOpen();
    }
    setSavingInstanceTz(false);
  };


  const handleUpdateLogoLink = (e) => {
    setCompanyLink(e.target.value);
  };

  const handleLogoLinkUpdate = () => {
    // Returned so SaveButton can await it and show progress.
    return setLogoLink(companyLink)
      .then(() => {
        setMessage('Successfully updated logo link');
        setMessageType('success');
        handleAlertOpen();
        window.location.reload();
      })
      .catch(() => {
        setMessage('Error updating logo link');
        setMessageType('error');
        handleAlertOpen();
      });
  };

  const handleFileUpload = (event) => {
    let pickedFile;
    if (event.target.files && event.target.files.length === 1) {
      pickedFile = event.target.files[0];
      setFile(pickedFile);
    }
  };

  const handleUpload = () => {
    hiddenFileInput.current.click();
  };

  const uploadLogo = () => {
    let formData = new FormData();
    formData.append('image', file);
    // Returned so SaveButton can await it and show progress.
    return uploadLogoAction(formData)
      .then((result) => {
        setMessage('Logo Uploaded Successfully');
        setMessageType('success');
        handleAlertOpen();
        setLogoUpdate(true);
        window.location.reload();
      })
      .catch((err) => {
        setMessage('Failed to upload company logo');
        setMessageType('error');
        handleAlertOpen();
      });
  };

  // const handleCancel = () => {
  //   window.location.reload()
  // };

  const handleManufacturerToggle = async (value) => {
    if (!value) return;
    
    setSwitchState(value === 'on' ? 1 : 0);
    await setManufacturerSwitchPosition(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handleDistributorToggle = async (value) => {
    if (!value) return;

    setDistributorSwitchState(value === 'on' ? 1 : 0);
    await setDistributorSwitchPosition(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handleRoomvoChange = async (value) => {
    if (!value) return;
    
    setRoomvoSwitchState(value === 'on' ? 1 : 0);
    await setRoomvoToggle(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handleRoomCalcChange = async (value) => {
    if (!value) return;
    
    setRoomCalcSwitchState(value === 'on' ? 1 : 0);
    await setRoomCalcSwitchPosition(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handleLocationChange = async (value) => {
    if (!value) return;

    setLocationSwitchState(value === 'on' ? 1 : 0);
    await setLocationSwitchPosition(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handleFreightChange = async (value) => {
    if (!value) return;

    setFreightSwitchState(value === 'on' ? 1 : 0);
    await setFreightSwitchPosition(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handleProdCheckoutChange = async (value) => {
    if (!value) return;
    
    setProdCheckoutSwitchState(value === 'on' ? 1 : 0);
    await setProdCheckoutSwitchPosition(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handleProdFavChange = async (value) => {
    if (!value) return;
    
    setProdFavSwitchState(value === 'on' ? 1 : 0);
    await setProdFavSwitchPosition(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handleProdCollectionChange = async (value) => {
    if (!value) return;
    
    setProdCollectionSwitchState(value === 'on' ? 1 : 0);
    await setShowCollectionToCustomer(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handleProdSpecsChange = async (value) => {
    if (!value) return;
    
    setProdSpecsSwitchState(value === 'on' ? 1 : 0);
    await setShowSpecsToCustomer(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handleProdReqSelChange = async (value) => {
    if (!value) return;
    
    setProdReqSelSwitchState(value === 'on' ? 1 : 0);
    await setShouldRequireOptionSelection(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handleProdReqLoginChange = async (value) => {
    if (!value) return;

    setProdReqLoginSwitchState(value === 'on' ? 1 : 0);
    await setShouldRequireLoginSelection(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handlePriceTierModeChange = async (value) => {
    if (!value) return;

    setPriceTierSwitchState(value === 'on' ? 1 : 0);
    await setPriceTierMode(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };



  const handleProdCombineStyleSizesChange = async (value) => {
    if (!value) return;
    
    setProdCombineStyleSizesSwitchState(value === 'on' ? 1 : 0);
    await setCombineStyleSizesSelection(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handleUserRegChange = async (value) => {
    if (!value) return;
    
    setUserRegSwitchState(value === 'on' ? 1 : 0);
    await setCustomerCreateAccountsSwitchPos(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handleNotificationsChange = async (value) => {
    if (!value) return;
    
    setNotificationsSwitchState(value === 'on' ? 1 : 0);
    await setNotificationsSwitchPos(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handleProdDiscoveryChange = async (value) => {
    if (!value) return;
    
    setProdDiscoverySwitchState(value === 'on' ? 1 : 0);
    await setShowProductDiscoverySelection(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const handleProdRelatedChange = async (value) => {
    if (!value) return;
    
    setProdRelatedSwitchState(value === 'on' ? 1 : 0);
    await setShowRelatedProductsSelection(value === 'on' ? '1' : '0');
    setRefreshData(true);
  };

  const renderImg = (style) => {
    return file === undefined ? (
      <img className="company-logo" style={style} src={companyLogo} alt="LOGO"></img>
    ) : (
      <img
        className="company-logo"
        style={style}
        src={file ? URL.createObjectURL(file) : null}
        alt={file ? file.name : null}
      />
    );
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleMaterialClassOpen = () => {
    setDialogSelect('materialClass');
    setOpen(true);
  };

  const handleUomOpen = () => {
    setDialogSelect('uom');
    setOpen(true);
  };

  const handleQrMarginOpen = () => {
    setDialogSelect('qr code margins');
    setOpen(true);
  };

  const handleQrStyleOpen = () => {
    setDialogSelect('qr styles');
    setOpen(true);
  };

  const handlePriceTierOpen = () => {
    setDialogSelect('price tiers');
    setOpen(true);
  };

  const handleCrmOpen = () => {
    setDialogSelect('integration');
    setOpen(true);
  };

  const submitLocationChanges = async () => {
    if (!selectedLocation) {
      setMessage('Failed to update location. No location selected');
      setMessageType('error');
      handleAlertOpen();

      return;
    }

    const location = locations?.find(l => l.location_id === selectedLocation);

    if (!location) {
      setMessage('Failed to update location. Location not found');
      setMessageType('error');
      handleAlertOpen();

      return;
    }

    updateLocation(
      location?.location_id,
      location?.location_name,
      location?.location_description,
      location?.sample_reminder,
      tempLocationName ? tempLocationName : null,
      tempLocationPhone ? tempLocationPhone : null,
      tempLocationEmail ? tempLocationEmail : null
    )
      .then((result) => {
        setMessage('Successfully updated location');
        setMessageType('success');
        handleAlertOpen();
        updateAdminContext(true);
        setRefreshData(true);
      })
      .catch((err) => {
        setMessage('Failed to update location');
        setMessageType('error');
        handleAlertOpen();
      });
  }

  const submitNotificationConfigChanges = async () => {
    if (!checkoutPeriod || !pastDueLimit || !pastDueFrequency || !dailyStart || !dailyEnd) {
      setMessage('Failed to update notification config. One of the settings is invalid');
      setMessageType('error');
      handleAlertOpen();

      return;
    }

    if (parseInt(dailyStart) >= parseInt(dailyEnd)) {
      setMessage('Failed to update notification config. the daily start needs to be less than the daily end');
      setMessageType('error');
      handleAlertOpen();

      return;
    }

    setSampleReminderConfig(checkoutPeriod, pastDueLimit, pastDueFrequency, dailyStart, dailyEnd)
      .then(result => {
        setMessage('Successfully updated the notification config!');
        setMessageType('success');
        handleAlertOpen();
        setRefreshData(true);
      })
      .catch(e => {
        setMessage('Failed to update notification config');
        setMessageType('error');
        handleAlertOpen();

        return;
      })
  }

  const getQConnectData = async () => {
    setIsCatalogProcessing(true);

    await getCatalogCsv()
      .then((result) => {
        setMessage("Qfloors catalog processed");
        setMessageType("success");
        handleAlertOpen();
      }).catch((err) => {
        setMessage("Error processing Qfloors catalog");
        setMessageType("error");
        handleAlertOpen();
      });

    setIsCatalogProcessing(false);
  }

  const updateCredentials = async () => {
    await setQconnectConfig({ username: qconnectUser, password: qconnectPass })
      .then((result) => {
        setMessage("Updated QConnect credentials");
        setMessageType("success");
        handleAlertOpen();
      }).catch((err) => {
        setMessage("Error updating QConnect credentials");
        setMessageType("error");
        handleAlertOpen();
      });
  }

  const renderDialog = () => {
    return (
      <Dialog
        onClose={handleClose}
        open={open}
        maxWidth="lg"
        style={{ display: 'flex', justifyContent: 'center' }}
      >
        <DialogTitle className="files-by-partner-header">
          {dialogSelect === 'materialClass'
            ? 'Material Class Codes'
            : dialogSelect === 'uom'
              ? 'Units of Measure'
              : dialogSelect === 'qr styles'
                ? 'QR Code Styles'
                : dialogSelect === 'price tiers'
                  ? 'Price Tiers'
                  : dialogSelect === 'integration'
                    ? 'Integrations'
                    : 'QR Print Margins'}
          <Button
            variant="outlined"
            style={{
              color: '#FF682C',
              marginLeft: 20,
              fontSize: 20,
              float: 'right',
              border: '1px solid #DCDCDC',
            }}
            onClick={handleClose}
          >
            X
          </Button>
        </DialogTitle>
        {dialogSelect === 'materialClass' ? (
          <MaterialClassSettings
            matClassCdArray={matClassCdArray}
            codeTranslationArray={codeTranslationArray}
          />
        ) : dialogSelect === 'uom' ? (
          <UomSettings
            uomArray={uomArray}
            codeTranslationArray={codeTranslationArray}
          />
        ) : dialogSelect === 'qr styles' ? (
          <QrStylePresets />
        ) : dialogSelect === 'price tiers' ? (
          <div style={{ padding: '0 24px 24px' }}>
            <PriceTierBands />
          </div>
        ) : dialogSelect === 'integration' ? (
          <div style={{ padding: '0 24px 24px' }}>
            <IntegrationSettings provider="albi" />
          </div>
        ) : (
          <QrPrintSetting margins={margins} />
        )}
      </Dialog>
    );
  };

  // Has-changed checks for every Save on this page. Each button is enabled, and
  // therefore green, only when pressing it would actually do something.
  const selectedLocationRow = locations?.find((l) => l.location_id === selectedLocation);
  const locationDirty = !!selectedLocation && (
    (tempLocationEmail ?? '') !== (selectedLocationRow?.location_email ?? '') ||
    (tempLocationPhone ?? '') !== (selectedLocationRow?.location_phone ?? '') ||
    (tempLocationName ?? '') !== (selectedLocationRow?.customer_facing_name ?? '')
  );
  const qconnectDirty =
    qconnectUser !== savedQconnect.username || qconnectPass !== savedQconnect.password;
  const notifConfigDirty = !savedNotifConfig || (
    String(checkoutPeriod) !== savedNotifConfig.checkoutPeriod ||
    String(pastDueLimit) !== savedNotifConfig.pastDueLimit ||
    String(pastDueFrequency) !== savedNotifConfig.pastDueFrequency ||
    String(dailyStart) !== savedNotifConfig.dailyStart ||
    String(dailyEnd) !== savedNotifConfig.dailyEnd
  );
  const logoLinkDirty = companyLink !== logoLink;

  // Status for the integrations list. Each system reports connectivity its own
  // way, so this is the single place that translates them into a common shape.
  // Refreshed on mount and whenever an integration dialog closes, so saving in
  // the dialog updates the row behind it.
  useEffect(() => {
    if (configuring) return;
    let cancelled = false;
    getIntegrationConfig('albi')
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data ?? {};
        setAlbiStatus({
          hasKey: !!d.has_api_key,
          enabled: !!d.enabled,
          mapped: d.mapped_contacts ?? 0,
        });
      })
      .catch(() => {
        // Non-fatal: the row just shows "Not connected" until the next attempt.
      });

    getBarcodeConfig()
      .then((res) => {
        if (!cancelled) setBarcodeEnabled(!!res.data?.data?.enabled);
      })
      .catch(() => {
        // Non-fatal: the row falls back to showing it as off.
      });
    return () => { cancelled = true; };
  }, [configuring]);

  const integrationRows = [
    {
      id: 'qconnect',
      name: 'QConnect',
      description: 'QFloors \u2014 imports your product catalog',
      connected: !!qconnectToggle,
      status: qconnectToggle ? 'Connected' : 'Off',
      detail: qconnectToggle && qconnectUser ? 'Credentials saved' : null,
    },
    {
      id: 'roomvo',
      name: 'Roomvo',
      description: 'Room visualizer on the product page',
      connected: !!roomvoSwitchState,
      status: roomvoSwitchState ? 'On' : 'Off',
      detail: null,
    },
    {
      id: 'barcode',
      name: 'RFMS Product Barcode',
      description: 'Scannable barcode on the product page for RFMS lookup',
      connected: barcodeEnabled,
      status: barcodeEnabled ? 'On' : 'Off',
      detail: null,
      configureHint: 'A barcode rendered on the product page, not an external service',
    },
    {
      id: 'albi',
      name: 'Albi',
      description: 'Restoration CRM \u2014 pulls contacts into your leads',
      connected: !!albiStatus.hasKey,
      status: albiStatus.hasKey ? (albiStatus.enabled ? 'Connected' : 'Key saved, sync off') : 'Not connected',
      detail: albiStatus.mapped ? `${albiStatus.mapped} contacts linked` : null,
      tourId: 'tour-integrations-btn',
    },
  ];

  // Roomvo and QConnect keep their original panels verbatim; they are rendered
  // into a dialog from the integrations list rather than sitting on the page.
  // Defined inline so they keep reading component state directly.
  // Rendered inside the settings dialog, so the Notifications tab stays a
  // single row like the other sections rather than a page-long form. The
  // on/off switch stays on the row: that is the control an admin actually
  // flips, while everything in here is setup you touch once.
  const renderNotificationSettings = () => (
    <div style={{ padding: '10px 0' }}>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 6 }}>
        When sample checkout reminders are sent, and the store details they carry.
      </Typography>
                  <div>
                    <div style={{ padding: '18px 20px 0' }}>
                      <Typography {...GROUP_HEADING}>Schedule</Typography>
                    </div>

                    <SettingRow dense label="Checkout period" help="How many days a customer may keep a sample before it counts as past due.">
                      <TextField size="small" type="number" variant="filled" inputProps={{ min: 0, style: { textAlign: 'right', width: 70 } }} value={checkoutPeriod} onChange={(e) => setCheckoutPeriod(e.target.value)} />
                      <Typography variant="caption" color="textSecondary">days</Typography>
                    </SettingRow>

                    <SettingRow dense label="Past due limit" help="Stop sending reminders once a sample is this many days past due.">
                      <TextField size="small" type="number" variant="filled" inputProps={{ min: 0, style: { textAlign: 'right', width: 70 } }} value={pastDueLimit} onChange={(e) => setPastDueLimit(e.target.value)} />
                      <Typography variant="caption" color="textSecondary">days</Typography>
                    </SettingRow>

                    <SettingRow dense label="Reminder frequency" help="How often to re-send a reminder while a sample is past due.">
                      <TextField size="small" type="number" variant="filled" inputProps={{ min: 0, style: { textAlign: 'right', width: 70 } }} value={pastDueFrequency} onChange={(e) => setPastDueFrequency(e.target.value)} />
                      <Typography variant="caption" color="textSecondary">days</Typography>
                    </SettingRow>

                    {/* Start and end were two separate stacked fields, which hid
                        the fact that they are one range. */}
                    <SettingRow dense label="Send between" help="Reminders are only sent inside this window, in Central time. Use whole hours on a 24-hour clock.">
                      <TextField size="small" type="number" variant="filled" inputProps={{ min: 0, style: { textAlign: 'right', width: 70 } }} value={dailyStart} onChange={(e) => setDailyStart(e.target.value)} />
                      <Typography variant="caption" color="textSecondary">and</Typography>
                      <TextField size="small" type="number" variant="filled" inputProps={{ min: 0, style: { textAlign: 'right', width: 70 } }} value={dailyEnd} onChange={(e) => setDailyEnd(e.target.value)} />
                      <Typography variant="caption" color="textSecondary">CST</Typography>
                    </SettingRow>

                    <div style={{ padding: '4px 20px 16px', textAlign: 'right' }}>
                      <SaveButton onClick={() => submitNotificationConfigChanges()} disabled={!notifConfigDirty} />
                    </div>

                    <Divider />

                    <div style={{ padding: '18px 20px 0' }}>
                      <Typography {...GROUP_HEADING}>Store contact details</Typography>
                      <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
                        Shown to customers in the reminders sent from this location.
                      </Typography>
                    </div>

                    <SettingRow dense label="Location" help="Pick a store, then edit the contact details reminders from it will carry.">
                      <FormControl size="small" variant="filled" style={{ minWidth: 260 }}>
                        <InputLabel id="locations-label">Location</InputLabel>
                        <Select
                          labelId="locations-label"
                          value={selectedLocation}
                          onChange={(e) => setSelectedLocation(e.target.value)}
                        >
                          <MenuItem value="">Please select a location</MenuItem>
                          {locations?.map((l) => (
                            <MenuItem key={l.location_id} value={l.location_id}>
                              {l.location_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </SettingRow>

                    <SettingRow dense label="Display name" help="The store name customers see in a reminder.">
                      <TextField size="small" variant="filled" style={{ width: 260 }} disabled={!selectedLocation} value={tempLocationName} onChange={(e) => setTempLocationName(e.target.value)} />
                    </SettingRow>

                    <SettingRow dense label="Email" help="Reply-to address on reminder emails from this location.">
                      <TextField size="small" variant="filled" style={{ width: 260 }} disabled={!selectedLocation} value={tempLocationEmail} onChange={(e) => setTempLocationEmail(e.target.value)} />
                    </SettingRow>

                    <SettingRow dense label="Phone" help="Contact number shown in reminders from this location.">
                      <TextField size="small" variant="filled" style={{ width: 260 }} disabled={!selectedLocation} value={tempLocationPhone} onChange={(e) => setTempLocationPhone(e.target.value)} />
                    </SettingRow>

                    <div style={{ padding: '4px 20px 16px', textAlign: 'right' }}>
                      <SaveButton onClick={() => submitLocationChanges()} disabled={!locationDirty} />
                    </div>
                  </div>
                  <Divider />

                  <div style={{ padding: '14px 20px', textAlign: 'right' }}>
                    <Button variant="outlined" size="small" onClick={() => setIsSampleReminderDialogOpen(true)}>
                      View Reminder Examples
                    </Button>
                  </div>
    </div>
  );
  const renderRoomvoPanel = () => (
    <div style={{ padding: '10px 0' }}>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 14 }}>
        Toggle the Roomvo room visualizer on or off for your product pages.
      </Typography>
      <Grid container spacing={1}>
            <Grid container spacing={1} style={{ textAlign: 'center' }}>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={5}
                style={{
                  textAlign: 'left',
                  margin: '10px auto',
                  padding: '0px 20px',
                }}
              >
                <Typography
                  variant="subtitle1"
                  style={{ fontWeight: 700 }}
                  className="admin__product-settings"
                  style={{ textAlign: 'center' }}
                >
                  Roomvo Visualizer
                </Typography>
                <Grid container spacing={2} style={{margin: "15px 10px"}}>
                  <Grid item xs={12} style={{textAlign: "center"}}>
                    <ToggleButtonGroup
                      value={roomvoSwitchState ? 'on' : 'off'}
                      exclusive
                      size='small'
                      onChange={(e, newValue) => handleRoomvoChange(newValue)}
                      aria-label="text alignment"
                      style={{display: "inline-block"}}
                    >
                      <ToggleButton value='on' aria-label="show roomvo button" style={roomvoSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                        Yes
                      </ToggleButton>
                      <ToggleButton value='off' aria-label="hide roomvo button" style={!roomvoSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                        No
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                      Show Roomvo Button On Product Page
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={6}
                lg={6}
                style={{
                  textAlign: 'left',
                  margin: '10px auto',
                  padding: '0px 20px',
                }}
              >
                <Typography
                  variant="subtitle1"
                  style={{ fontWeight: 700 }}
                  className="admin__product-settings"
                  style={{ textAlign: 'left' }}
                >
                  Roomvo Instructions
                </Typography>
                <Typography style={{ margin: '10px 0px 0px' }}>
                  <b>Already a Roomvo Customer?</b>
                </Typography>
                <List style={{ padding: '0px' }}>
                  <ListItem>
                    <ListItemText>
                      Please contact our support team and let them know you
                      already utilize Roomvo. There are extra steps that we need
                      to follow on the backend to ensure the integration works
                      correctly.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography style={{ margin: '10px 0px 0px' }}>
                  <b>New to Roomvo?</b>
                </Typography>
                <List style={{ padding: '0px' }}>
                  <ListItem>
                    <ListItemText>
                      1. If you have not signed up with roomvo already, please
                      go to the{' '}
                      <a
                        href="https://www.roomvo.com/share/ShowroomPricing"
                        target="_blank"
                      >
                        signup page
                      </a>
                      .
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      2. Make sure your Showroom Pricing URL is added to your
                      Roomvo account.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography>
                  <b>
                    Please note: Roomvo links will be on all product pages and
                    is based on the product SKU. If a SKU is not in Roomvo's
                    system then a random product will be shown instead.{' '}
                    <u>
                      This is a limitation with Roomvo and not Showroom Pricing.
                    </u>
                  </b>
                </Typography>
              </Grid>
            </Grid>
      </Grid>
    </div>
  );

  const renderQconnectPanel = () => (
    <div style={{ padding: '10px 0' }}>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 14 }}>
        Change your QConnect credentials and import your catalog from QFloors. Imports appear in the Manual Files section of the home page.
      </Typography>
      <Grid container spacing={1}>
                <Grid container spacing={1} style={{textAlign: "center"}}>
                  <Grid item xs={12} sm={12} md={6} lg={5} style={{textAlign: "left", margin: "10px auto", padding: "0px 20px"}}>
                    <Typography variant="subtitle1" style={{ fontWeight: 700 }} className="admin__product-settings" style={{textAlign: "left"}}>
                      QConnect Credentials
                    </Typography>
                    <div style={{margin: "10px 0px 0px", maxWidth: "300px"}}>
                      <TextField
                        label={"Username"}
                        placeholder={"Username"}
                        type={"text"}
                        variant="filled"
                        value={qconnectUser}
                        required={true}
                        style={{width: "100%", margin: "10px auto"}}
                        onChange={(e) => setQconnectUser(e.target.value)}
                      />
                      <TextField
                        label={"Password"}
                        placeholder={"Password"}
                        type={"password"}
                        variant="filled"
                        value={qconnectPass}
                        required={true}
                        style={{width: "100%", margin: "10px auto"}}
                        onChange={(e) => setQconnectPass(e.target.value)}
                      />
                    </div>
                    <SaveButton onClick={() => updateCredentials()} disabled={!qconnectDirty} />
                    <Button variant="contained" onClick={() => getQConnectData()} style={{display: "inline-block", backgroundColor: "#4caf50", color: "#FFFFFF", margin: "30px 20px 20px 0px"}}>
                      { isCatalogProcessing ? (
                          <><CircularProgress size={18} style={{color: "#FFFFFF", display: "inline-block", verticalAlign: "text-top", marginRight: "5px"}}/> Processing...</>
                        ) : 'Process Catalog'
                      }
                    </Button>
                    <Typography style={{margin: "0px 0px 40px"}}>
                      * Please wait for a notification in the top right corner of the page after pressing the Process Catalog button.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={6} style={{textAlign: "left", margin: "10px auto", padding: "0px 20px"}}>
                    <Typography variant="subtitle1" style={{ fontWeight: 700 }} className="admin__product-settings" style={{textAlign: "left"}}>
                      QConnect Instructions
                    </Typography>
                    <Typography style={{margin: "10px 0px"}}>
                      Please ensure the following steps are completed during the initial setup of QConnect.
                    </Typography>
                    <List>
                      <ListItem >
                        <ListItemText>1. Go to the QConnect settings and enter in your QConnect username and password.</ListItemText>
                      </ListItem>
                      <ListItem >
                        <ListItemText>2. Click on the "Send Showroom Pricing CSV" checkbox.</ListItemText>
                      </ListItem>
                      <ListItem >
                        <ListItemText>3. Tag the products in the QFloors product catalog you would like to transfer to Showroom Pricing. <b>Please note: The limit is 25,000 and it is best to only transfer non-B2B vendors through QConnect.</b></ListItemText>
                      </ListItem>
                      <ListItem >
                        <ListItemText>4. Each product has to have an SKU number in QFloors.</ListItemText>
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
      </Grid>
    </div>
  );

  if (isSettingsDataLoading) {
    return (
      <Backdrop open={true}>
        <CircularProgress size={100} style={{color: "#FFFFFF"}}/>
      </Backdrop>
    )
  }

  return (
    <div className="settings__container">
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={3} lg={3} style={{ paddingTop: 50 }}>
          <SettingsNav sections={SECTIONS} active={section} onChange={setSection} />
        </Grid>
        <Grid item xs={12} sm={12} md={9} lg={9}>
        { section === 'general' ? (
            <Grid container spacing={3}>
              <Grid item xs={12} className="admin__grid-item">
                <Paper elevation={5}>
                  <Typography variant="h4" className="header-text">
                    General
                  </Typography>

                  {/* The file input is hidden but must stay mounted — the
                      Browse button clicks it through hiddenFileInput. */}
                  <input
                    className="admin__bulletin-image-uploader"
                    type="file"
                    ref={hiddenFileInput}
                    onChange={handleFileUpload}
                    accept="image/x-png"
                  />

                  <SettingRow label="Company Logo" help="Upload the logo shown to customers on the product page and on printed QR labels. PNG works best.">
                    <div style={{ display: 'flex', alignItems: 'center', maxWidth: 170, overflow: 'hidden' }}>
                      {renderImg({ maxHeight: 36, maxWidth: 170, objectFit: 'contain' })}
                    </div>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleUpload}
                    >
                      Browse
                    </Button>
                    <SaveButton onClick={uploadLogo} disabled={!file} />
                  </SettingRow>

                  <SettingRow label="Logo Link" help="Where the logo links to when a customer clicks it.">
                    <TextField
                      variant="filled"
                      name="logolink"
                      size="small"
                      placeholder="http://www.yoursite.com"
                      value={companyLink}
                      onChange={handleUpdateLogoLink}
                      style={{ width: 280 }}
                    />
                    <SaveButton onClick={handleLogoLinkUpdate} disabled={!logoLinkDirty} />
                  </SettingRow>

                  <SettingRow label="New User Registration" help="Allow customers to create their own account.">
                    <ToggleButtonGroup
                      value={userRegSwitchState ? 'on' : 'off'}
                      exclusive
                      size="small"
                      onChange={(e, newValue) => handleUserRegChange(newValue)}
                      aria-label="allow new user registration"
                    >
                      <ToggleButton value="on" aria-label="allow new user registration" style={userRegSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                        Yes
                      </ToggleButton>
                      <ToggleButton value="off" aria-label="deny new user registration" style={!userRegSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                        No
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </SettingRow>

                  <SettingRow
                    label="Default Timezone"
                    help="Timestamps across the app use this for anyone who has not chosen their own. Existing timestamps were recorded in Central time and are reinterpreted for display — stored data is never changed, so historical dates stay accurate."
                    id="tour-timezone-setting"
                    last
                  >
                    <div style={{ width: 280 }}>
                      <TimezonePicker
                        value={instanceTimezone}
                        onChange={setInstanceTimezone}
                        label="Company default timezone"
                      />
                    </div>
                    <SaveButton
                      onClick={handleInstanceTimezoneSave}
                      disabled={!instanceTimezone || instanceTimezone === defaultZone}
                    />
                  </SettingRow>
                </Paper>
              </Grid>
            </Grid>
          ) : undefined }
        { section === 'productPage' ? (
            <Grid container spacing={3}>
        <Grid item xs={12} className="admin__grid-item">
          <Paper elevation={5}>
            <Typography variant="h4" className="header-text">
              Product Page Settings
            </Typography>
            <Grid container spacing={2} style={{margin: "15px 10px"}}>
              <Grid item xs={12}>
                <ToggleButtonGroup
                  value={prodReqLoginSwitchState ? 'on' : 'off'}
                  exclusive
                  size='small'
                  onChange={(e, newValue) => handleProdReqLoginChange(newValue)}
                  aria-label="text alignment"
                  style={{display: "inline-block"}}
                >
                  <ToggleButton value='on' aria-label="require login for pricing" style={prodReqLoginSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    Yes
                  </ToggleButton>
                  <ToggleButton value='off' aria-label="dont require login for pricing" style={!prodReqLoginSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    No
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                  Require Login for Pricing
                </Typography>
              </Grid>
              <Grid item xs={12} id="tour-price-tier-toggle">
                <ToggleButtonGroup
                  value={priceTierSwitchState ? 'on' : 'off'}
                  exclusive
                  size='small'
                  onChange={(e, newValue) => handlePriceTierModeChange(newValue)}
                  aria-label="price tier mode"
                  style={{display: "inline-block"}}
                >
                  <ToggleButton value='on' aria-label="show price tiers to customers" style={priceTierSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    Yes
                  </ToggleButton>
                  <ToggleButton value='off' aria-label="show prices to customers" style={!priceTierSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    No
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                  Show Price Tiers Instead of Prices
                </Typography>
                <Button
                  id="tour-price-tiers-btn"
                  size="small"
                  variant="outlined"
                  onClick={handlePriceTierOpen}
                  style={{marginLeft: "10px"}}
                >
                  Edit Tiers
                </Button>
                {priceTierSwitchState ? (
                  <Typography variant="caption" color="textSecondary" style={{display: "block", marginTop: "4px"}}>
                    {'Customers and anyone not logged in see a colored tier instead of a price when they scan a product.'}
                    {prodReqLoginSwitchState
                      ? ' This overrides "Require Login for Pricing" — customers see a tier rather than "Ask For Price".'
                      : ''}
                  </Typography>
                ) : undefined}
              </Grid>
              <Grid item xs={12}>
                <ToggleButtonGroup
                  value={switchState ? 'on' : 'off'}
                  exclusive
                  size='small'
                  onChange={(e, newValue) => handleManufacturerToggle(newValue)}
                  aria-label="text alignment"
                  style={{display: "inline-block"}}
                >
                  <ToggleButton value='on' aria-label="show manufacturer" style={switchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    Yes
                  </ToggleButton>
                  <ToggleButton value='off' aria-label="hide manufacturer" style={!switchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    No
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                  Show Manufacturer
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ToggleButtonGroup
                  value={distributorSwitchState ? 'on' : 'off'}
                  exclusive
                  size='small'
                  onChange={(e, newValue) => handleDistributorToggle(newValue)}
                  aria-label="text alignment"
                  style={{display: "inline-block"}}
                >
                  <ToggleButton value='on' aria-label="show distributor" style={distributorSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    Yes
                  </ToggleButton>
                  <ToggleButton value='off' aria-label="hide distributor" style={!distributorSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    No
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                  Show Distributor
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ToggleButtonGroup
                  value={roomCalcSwitchState ? 'on' : 'off'}
                  exclusive
                  size='small'
                  onChange={(e, newValue) => handleRoomCalcChange(newValue)}
                  aria-label="text alignment"
                  style={{display: "inline-block"}}
                >
                  <ToggleButton value='on' aria-label="show room calc" style={roomCalcSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    Yes
                  </ToggleButton>
                  <ToggleButton value='off' aria-label="hide room calc" style={!roomCalcSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    No
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                  Show Room Calculator
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ToggleButtonGroup
                  value={freightSwitchState ? 'on' : 'off'}
                  exclusive
                  size='small'
                  onChange={(e, newValue) => handleFreightChange(newValue)}
                  aria-label="text alignment"
                  style={{display: "inline-block"}}
                >
                  <ToggleButton value='on' aria-label="show freight" style={freightSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    Yes
                  </ToggleButton>
                  <ToggleButton value='off' aria-label="hide freight" style={!freightSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    No
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                  Show Freight &amp; Tariffs
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ToggleButtonGroup
                  value={locationSwitchState ? 'on' : 'off'}
                  exclusive
                  size='small'
                  onChange={(e, newValue) => handleLocationChange(newValue)}
                  aria-label="text alignment"
                  style={{display: "inline-block"}}
                >
                  <ToggleButton value='on' aria-label="show location" style={locationSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    Yes
                  </ToggleButton>
                  <ToggleButton value='off' aria-label="hide location" style={!locationSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    No
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                  Show Location
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ToggleButtonGroup
                  value={prodCheckoutSwitchState ? 'on' : 'off'}
                  exclusive
                  size='small'
                  onChange={(e, newValue) => handleProdCheckoutChange(newValue)}
                  aria-label="text alignment"
                  style={{display: "inline-block"}}
                >
                  <ToggleButton value='on' aria-label="allow checkouts" style={prodCheckoutSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    Yes
                  </ToggleButton>
                  <ToggleButton value='off' aria-label="no checkouts" style={!prodCheckoutSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    No
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                  Allow Sample Checkouts
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ToggleButtonGroup
                  value={prodFavSwitchState ? 'on' : 'off'}
                  exclusive
                  size='small'
                  onChange={(e, newValue) => handleProdFavChange(newValue)}
                  aria-label="text alignment"
                  style={{display: "inline-block"}}
                >
                  <ToggleButton value='on' aria-label="allow favorites list" style={prodFavSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    Yes
                  </ToggleButton>
                  <ToggleButton value='off' aria-label="no favorites list" style={!prodFavSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                    No
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                  Allow Favorites List
                </Typography>
              </Grid>

              { (
                  <>
                    <Grid item xs={12}>
                      <ToggleButtonGroup
                        value={prodSpecsSwitchState ? 'on' : 'off'}
                        exclusive
                        size='small'
                        onChange={(e, newValue) => handleProdSpecsChange(newValue)}
                        aria-label="text alignment"
                        style={{display: "inline-block"}}
                      >
                        <ToggleButton value='on' aria-label="show collection" style={prodSpecsSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                          Yes
                        </ToggleButton>
                        <ToggleButton value='off' aria-label="hide collection" style={!prodSpecsSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                          No
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                        Show Product Specs
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <ToggleButtonGroup
                        value={prodCollectionSwitchState ? 'on' : 'off'}
                        exclusive
                        size='small'
                        onChange={(e, newValue) => handleProdCollectionChange(newValue)}
                        aria-label="text alignment"
                        style={{display: "inline-block"}}
                      >
                        <ToggleButton value='on' aria-label="show collection" style={prodCollectionSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                          Yes
                        </ToggleButton>
                        <ToggleButton value='off' aria-label="hide collection" style={!prodCollectionSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                          No
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                        Show Collection
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <ToggleButtonGroup
                        value={prodRelatedSwitchState ? 'on' : 'off'}
                        exclusive
                        size='small'
                        onChange={(e, newValue) => handleProdRelatedChange(newValue)}
                        aria-label="text alignment"
                        style={{display: "inline-block"}}
                      >
                        <ToggleButton value='on' aria-label="show related products" style={prodRelatedSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                          Yes
                        </ToggleButton>
                        <ToggleButton value='off' aria-label="hide related products" style={!prodRelatedSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                          No
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                        Show Related Products
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <ToggleButtonGroup
                        value={prodDiscoverySwitchState ? 'on' : 'off'}
                        exclusive
                        size='small'
                        onChange={(e, newValue) => handleProdDiscoveryChange(newValue)}
                        aria-label="text alignment"
                        style={{display: "inline-block"}}
                      >
                        <ToggleButton value='on' aria-label="show product discovery" style={prodDiscoverySwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                          Yes
                        </ToggleButton>
                        <ToggleButton value='off' aria-label="hide product discovery" style={!prodDiscoverySwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                          No
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                        Show Product Discovery (Other Products You Might Like)
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <ToggleButtonGroup
                        value={prodReqSelSwitchState ? 'on' : 'off'}
                        exclusive
                        size='small'
                        onChange={(e, newValue) => handleProdReqSelChange(newValue)}
                        aria-label="text alignment"
                        style={{display: "inline-block"}}
                      >
                        <ToggleButton value='on' aria-label="require initial option selection" style={prodReqSelSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                          Yes
                        </ToggleButton>
                        <ToggleButton value='off' aria-label="dont require initial option selection" style={!prodReqSelSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                          No
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                        Require Initial Option Selection
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <ToggleButtonGroup
                        value={prodCombineStyleSizesSwitchState ? 'on' : 'off'}
                        exclusive
                        size='small'
                        onChange={(e, newValue) => handleProdCombineStyleSizesChange(newValue)}
                        aria-label="text alignment"
                        style={{display: "inline-block"}}
                      >
                        <ToggleButton value='on' aria-label="show style sizes on one page" style={prodCombineStyleSizesSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                          Yes
                        </ToggleButton>
                        <ToggleButton value='off' aria-label="dont show style sizes on one page" style={!prodCombineStyleSizesSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                          No
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <Typography variant="body1" style={{display: "inline-block", marginLeft: "10px"}}>
                        Show All Style Sizes On One Page
                      </Typography>
                    </Grid>
                  </>
                )
              }
            </Grid>

            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
              >
                <Typography variant="body1" style={{ fontWeight: 600 }}>Product Page Settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Manufacturer"
                      secondary="Selector to show manufacturer on product page."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Distributor"
                      secondary="Selector to show distributor on product page. Internal users always see it."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Room Calculator"
                      secondary="Selector to show room size calculator on product page."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Location"
                      secondary="Selector to show location on product page."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Sample Checkouts"
                      secondary="Selector to show product checkout on product page."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Favorites List"
                      secondary="Selector to show product favorite on product page."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Product Specs"
                      secondary="Selector to show product specs on product page."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Collection"
                      secondary="Selector to show product collection on product page."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Require Initial Option Selection"
                      secondary="Selector to require user to select an option to see pricing on product page."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Combine Like Sizes"
                      secondary="Selector to determine if all sizes for a style should show on one product page."
                    />
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
            </Grid>
          ) : undefined }

        { section === 'qrPrint' ? (
            <Grid container spacing={3}>
              <Grid item xs={12} className="admin__grid-item">
                <Paper id="tour-qr-settings" elevation={5}>
                  <Typography variant="h4" className="header-text">
                    QR Print Settings
                  </Typography>

                  {/* One row per thing you can open, matching the integrations
                      list. The two buttons used to sit side by side with their
                      explanation folded away in an accordion, so neither said
                      what it did. */}
                  <SettingRow
                    label="QR Code Styles"
                    help="Create and manage named style presets — colours, dot patterns, logo, label text and price-based colours — that staff choose between when printing."
                  >
                    <Button id="tour-qr-styles-btn" variant="outlined" size="small" style={{ borderColor: '#4E564E', color: '#4E564E' }} onClick={handleQrStyleOpen}>
                      Configure
                    </Button>
                  </SettingRow>

                  <SettingRow
                    label="Print Margins"
                    help="Adjust where labels sit on the sheet so printing lines up with your label stock."
                    last
                  >
                    <Button variant="outlined" size="small" style={{ borderColor: '#4E564E', color: '#4E564E' }} onClick={handleQrMarginOpen}>
                      Configure
                    </Button>
                  </SettingRow>
                </Paper>
              </Grid>
            </Grid>
          ) : undefined }

        { section === 'integrations' ? (
            <Grid container spacing={3}>
              <Grid item xs={12} className="admin__grid-item">
                <IntegrationsPanel integrations={integrationRows} onConfigure={setConfiguring} />
              </Grid>
            </Grid>
          ) : undefined }

        { section === 'notifications' ? (
            <Grid container spacing={3}>
              <Grid item xs={12} className="admin__grid-item">
                <Paper elevation={5}>
                  <Typography variant="h4" className="header-text">
                    User Notification Settings
                  </Typography>
                  <SettingRow
                    label="Sample Checkout Reminders"
                    help="Email and text reminders to customers who have samples out. Configure sets when they go and what store details they carry."
                    last
                  >
                    <ToggleButtonGroup
                      value={notificationsSwitchState ? 'on' : 'off'}
                      exclusive
                      size="small"
                      onChange={(e, newValue) => handleNotificationsChange(newValue)}
                      aria-label="allow sample checkout reminders"
                    >
                      <ToggleButton value="on" aria-label="send sample reminders" style={notificationsSwitchState ? {width: "50px", backgroundColor: "#4caf50", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                        Yes
                      </ToggleButton>
                      <ToggleButton value="off" aria-label="dont send sample reminders" style={!notificationsSwitchState ? {width: "50px", backgroundColor: "#f44336", color: "#FFFFFF"} : {width: "50px", backgroundColor: "#00000017", color: "initial"}}>
                        No
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Button
                      variant="outlined"
                      size="small"
                      style={{ borderColor: '#4E564E', color: '#4E564E', marginLeft: 8 }}
                      onClick={() => setConfiguring('notifications')}
                    >
                      Configure
                    </Button>
                  </SettingRow>
                </Paper>
              </Grid>
            </Grid>
          ) : undefined }

            <Dialog open={isSampleReminderDialogOpen} onClose={() => setIsSampleReminderDialogOpen(false)}>
              <Paper>
                <Typography
                  className="header-text"
                  variant="h4"
                >
                  <b>Example Reminders</b>
                </Typography>
                <div style={{padding: "20px"}}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" style={{marginBottom: "20px", fontWeight: 700}}><b>Email</b></Typography>
                      <Typography style={{marginBottom: "10px"}}>{"{{ First Name }}"},</Typography>
                      <Typography style={{marginBottom: "10px"}}>This is a friendly reminder you checked out the following flooring samples from {"{{ Location Name }}"}. These samples are due back to our store today or next business day.</Typography>
                      <List style={{padding: "0px", marginBottom: "10px"}}>
                        <ListItem style={{padding: "0px 20px"}}>
                          <ListItemText>- {"{{ Sample 1 Name }}"}</ListItemText>
                        </ListItem>
                        <ListItem style={{padding: "0px 20px"}}>
                          <ListItemText>- {"{{ Sample 2 Name }}"}</ListItemText>
                        </ListItem>
                      </List>
                      <Typography style={{marginBottom: "10px"}}>If you have any questions please contact the store at {"{{ Location Number }}"}.</Typography>
                      <Typography>Thank you,</Typography>
                      <Typography>{"{{ Location Name }}"}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" style={{marginBottom: "20px", fontWeight: 700}}><b>Text Message</b></Typography>
                      <Typography>{"{{ First Name }}"}, this is a reminder that the 3 samples you checked out from {"{{ Location Name }}"} are due back to our store today or next business day. For questions please call the store at {"{{ Location Number }}"}.</Typography>
                    </Grid>
                    <Grid item xs={12} style={{marginTop: "20px", textAlign: "right"}}>
                      <Button
                        variant="contained"
                        onClick={() => setIsSampleReminderDialogOpen(false)}
                      >
                        Close
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              </Paper>
            </Dialog>
        { section === 'imports' ? (
            <Grid container spacing={3}>
              <Grid item xs={12} className="admin__grid-item">
                <Paper elevation={5}>
                  <Typography variant="h4" className="header-text">
                    Import Settings
                  </Typography>

                  {/* Was two buttons side by side under a shared "Material
                      Class/UOM" heading, with the explanation folded into an
                      accordion. One row each, help on the row it describes. */}
                  <SettingRow
                    label="Material Classes"
                    help="The material classes and class codes used to categorise products as they are imported."
                  >
                    <Button variant="outlined" size="small" style={{ borderColor: '#4E564E', color: '#4E564E' }} onClick={handleMaterialClassOpen}>
                      Configure
                    </Button>
                  </SettingRow>

                  <SettingRow
                    label="Units of Measure"
                    help="The UOM types products can be priced and sold in, and the codes that map to them on import."
                    last
                  >
                    <Button variant="outlined" size="small" style={{ borderColor: '#4E564E', color: '#4E564E' }} onClick={handleUomOpen}>
                      Configure
                    </Button>
                  </SettingRow>
                </Paper>
              </Grid>
            </Grid>
          ) : undefined }
        </Grid>
      </Grid>

      {/* Each integration opens its own settings, unchanged, in a dialog. */}
      <Dialog onClose={() => setConfiguring(null)} open={!!configuring} maxWidth="lg" fullWidth>
        <DialogTitle className="files-by-partner-header">
          {INTEGRATION_TITLES[configuring] ?? 'Configure'}
          <Button
            variant="outlined"
            style={{ color: '#FF682C', marginLeft: 20, fontSize: 20, float: 'right', border: '1px solid #DCDCDC' }}
            onClick={() => setConfiguring(null)}
          >
            X
          </Button>
        </DialogTitle>
        <div style={{ padding: '0 24px 24px' }}>
          { configuring === 'albi' ? <IntegrationSettings provider="albi" /> : undefined }
          { configuring === 'qconnect' ? renderQconnectPanel() : undefined }
          { configuring === 'roomvo' ? renderRoomvoPanel() : undefined }
          { configuring === 'barcode' ? <BarcodeSettings /> : undefined }
          { configuring === 'notifications' ? renderNotificationSettings() : undefined }
        </div>
      </Dialog>
      {renderDialog()}
    </div>
  );
};

export default LogoLinkUpdate;
